import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { KeyRound, Loader2, ShieldCheck, Calculator, Save, Users, Plus, Pencil, Trash2, X, Check, Store, Link2, Unlink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#0d0f17] border border-slate-800 focus:border-emerald-500 rounded-xl px-4 py-3.5 text-sm text-white placeholder:text-slate-500 outline-none transition-colors";

const UsersSection = ({ onUnauthorized }) => {
  const [users, setUsers] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${localStorage.getItem("seed_admin_token")}` };

  const load = () => {
    axios.get(`${API}/users`, { headers })
      .then((res) => setUsers(res.data))
      .catch((e) => { if (e.response?.status === 401) onUnauthorized(); });
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (form.id) {
        const payload = { nome: form.nome, role: form.role, ativo: !!form.ativo };
        if (form.senha) payload.senha = form.senha;
        await axios.put(`${API}/users/${form.id}`, payload, { headers });
        toast.success("Conta atualizada.");
      } else {
        await axios.post(`${API}/users`, { nome: form.nome, email: form.email, senha: form.senha, role: form.role }, { headers });
        toast.success("Conta criada — já pode entrar no painel.");
      }
      setForm(null);
      load();
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
      else toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Erro ao salvar conta.");
    } finally { setSaving(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Excluir a conta de ${u.nome || u.email}?`)) return;
    try {
      await axios.delete(`${API}/users/${u.id}`, { headers });
      setUsers((us) => us.filter((x) => x.id !== u.id));
      toast.success("Conta excluída.");
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Erro ao excluir conta.");
    }
  };

  return (
    <div className="glass rounded-3xl p-8 space-y-4" data-testid="users-section">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-400 flex items-center gap-2">
          <Users className="w-4 h-4 text-emerald-400" />
          Contas de acesso ao painel.
        </p>
        <button
          onClick={() => setForm({ nome: "", email: "", senha: "", role: "gerente", ativo: true })}
          data-testid="user-new-button"
          className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 text-xs font-bold px-4 py-2 rounded-full transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> Nova conta
        </button>
      </div>

      {form && (
        <form onSubmit={save} className="space-y-3 border border-slate-800 rounded-2xl p-4" data-testid="user-form">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-white">{form.id ? "Editar conta" : "Nova conta"}</p>
            <button type="button" onClick={() => setForm(null)} className="text-slate-400 hover:text-white" data-testid="user-form-close"><X className="w-4 h-4" /></button>
          </div>
          <input className={inputCls} required placeholder="Nome*" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="user-input-nome" />
          {!form.id && (
            <input className={inputCls} type="email" required placeholder="E-mail de acesso*" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-input-email" />
          )}
          <input
            className={inputCls} type="password" required={!form.id}
            placeholder={form.id ? "Nova senha (deixe vazio para manter)" : "Senha (mín. 6 caracteres)*"}
            value={form.senha} onChange={(e) => setForm({ ...form, senha: e.target.value })}
            data-testid="user-input-senha"
          />
          <div className="grid grid-cols-2 gap-3 items-center">
            <select className={inputCls} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-input-role">
              <option value="gerente">Gerente — sem Financeiro/Custos</option>
              <option value="admin">Admin — acesso total</option>
            </select>
            {form.id && (
              <label className="flex items-center gap-2 text-xs text-slate-300">
                <input type="checkbox" checked={!!form.ativo} onChange={(e) => setForm({ ...form, ativo: e.target.checked })} data-testid="user-input-ativo" className="accent-emerald-500 w-4 h-4" />
                Conta ativa
              </label>
            )}
          </div>
          <button type="submit" disabled={saving} data-testid="user-save-button" className="w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-emerald-950 font-bold px-6 py-3.5 rounded-full transition-colors">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {form.id ? "Salvar alterações" : "Criar conta"}
          </button>
        </form>
      )}

      {users === null ? (
        <div className="h-20 rounded-xl bg-[#131620] animate-pulse" />
      ) : (
        <ul className="space-y-2" data-testid="users-list">
          {users.map((u) => (
            <li key={u.id} className="flex items-center gap-3 bg-[#0d0f17] rounded-xl px-4 py-3" data-testid={`user-item-${u.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate flex items-center gap-2">
                  {u.nome || u.email}
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${u.role === "admin" ? "bg-emerald-500/15 text-emerald-400" : "bg-sky-500/15 text-sky-400"}`}>
                    {u.role === "admin" ? "Admin" : "Gerente"}
                  </span>
                  {!u.ativo && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-500/15 text-slate-400">Inativa</span>}
                </p>
                <p className="text-[11px] text-slate-500 truncate">{u.email}</p>
              </div>
              <button
                onClick={() => setForm({ id: u.id, nome: u.nome, email: u.email, senha: "", role: u.role, ativo: u.ativo })}
                data-testid={`user-edit-${u.id}`}
                className="w-9 h-9 rounded-full bg-emerald-500/15 flex items-center justify-center text-emerald-400 hover:bg-emerald-500/30 transition-colors" title="Editar / redefinir senha"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => remove(u)} data-testid={`user-delete-${u.id}`}
                className="w-9 h-9 rounded-full bg-red-500/10 flex items-center justify-center text-red-400 hover:bg-red-500/25 transition-colors" title="Excluir"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const AccountTab = ({ onUnauthorized, role = "admin" }) => {
  const [form, setForm] = useState({ senha_atual: "", nova_senha: "", confirmar: "" });
  const [saving, setSaving] = useState(false);
  const [sim, setSim] = useState({ taxa_am: "", tac: "" });
  const [savingSim, setSavingSim] = useState(false);
  const [mlStatus, setMlStatus] = useState(null);
  const [mlBusy, setMlBusy] = useState(false);
  const [loja, setLoja] = useState({ cidade: "", uf: "", cep: "" });
  const [savingLoja, setSavingLoja] = useState(false);
  const headers = { Authorization: `Bearer ${localStorage.getItem("seed_admin_token")}` };

  const loadMlStatus = useCallback(() => {
    if (role !== "admin") return;
    axios.get(`${API}/ml/status`, { headers })
      .then((res) => setMlStatus(res.data))
      .catch(() => setMlStatus({ connected: false }));
    axios.get(`${API}/config/loja`)
      .then((res) => setLoja({ cidade: res.data.cidade || "", uf: res.data.uf || "", cep: res.data.cep || "" }))
      .catch(() => {});
  }, [role]);

  const saveLoja = async (e) => {
    e.preventDefault();
    setSavingLoja(true);
    try {
      await axios.put(`${API}/config/loja`, loja, { headers });
      toast.success("Endereço da loja salvo! Será usado nos anúncios do Mercado Livre.");
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
      else toast.error(err.response?.data?.detail || "Erro ao salvar endereço.");
    } finally { setSavingLoja(false); }
  };

  useEffect(() => {
    axios.get(`${API}/config/simulador`)
      .then((res) => setSim({ taxa_am: res.data.taxa_am, tac: res.data.tac }))
      .catch(() => {});
    loadMlStatus();
  }, [loadMlStatus]);

  const connectMl = async () => {
    setMlBusy(true);
    try {
      const res = await axios.get(`${API}/ml/oauth/start`, { headers });
      window.location.assign(res.data.authorize_url);
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
      else toast.error(err.response?.data?.detail || "Erro ao iniciar conexão com o Mercado Livre.");
      setMlBusy(false);
    }
  };

  const disconnectMl = async () => {
    if (!window.confirm("Desconectar a conta do Mercado Livre? Anúncios já publicados continuam no ar.")) return;
    setMlBusy(true);
    try {
      await axios.post(`${API}/ml/disconnect`, {}, { headers });
      setMlStatus({ connected: false });
      toast.success("Conta do Mercado Livre desconectada.");
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
      else toast.error("Erro ao desconectar.");
    } finally { setMlBusy(false); }
  };

  const saveSim = async (e) => {
    e.preventDefault();
    setSavingSim(true);
    try {
      const res = await axios.put(
        `${API}/config/simulador`,
        { taxa_am: Number(sim.taxa_am), tac: Number(sim.tac) },
        { headers }
      );
      setSim({ taxa_am: res.data.taxa_am, tac: res.data.tac });
      toast.success("Simulador atualizado — já vale no site.");
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
      else toast.error(err.response?.data?.detail || "Erro ao salvar configuração.");
    } finally { setSavingSim(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.nova_senha.length < 6) return toast.error("A nova senha precisa de ao menos 6 caracteres.");
    if (form.nova_senha !== form.confirmar) return toast.error("A confirmação não confere.");
    setSaving(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { senha_atual: form.senha_atual, nova_senha: form.nova_senha },
        { headers }
      );
      toast.success("Senha alterada com sucesso!");
      setForm({ senha_atual: "", nova_senha: "", confirmar: "" });
    } catch (err) {
      if (err.response?.status === 401) {
        const d = err.response?.data?.detail;
        if (d === "Senha atual incorreta") toast.error(d);
        else onUnauthorized();
      } else toast.error("Erro ao trocar a senha.");
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-md space-y-8">
      <div>
        <h1 className="font-display text-2xl font-bold text-white mb-6">{role === "admin" ? "Conta do administrador" : "Minha conta"}</h1>
        <form onSubmit={submit} className="glass rounded-3xl p-8 space-y-4" data-testid="change-password-form">
          <p className="text-sm text-slate-400 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-emerald-400" />
            Troque a senha de acesso ao painel.
          </p>
          <input
            type="password" required className={inputCls} placeholder="Senha atual"
            value={form.senha_atual} onChange={(e) => setForm({ ...form, senha_atual: e.target.value })}
            data-testid="cp-input-atual"
          />
          <input
            type="password" required className={inputCls} placeholder="Nova senha (mín. 6 caracteres)"
            value={form.nova_senha} onChange={(e) => setForm({ ...form, nova_senha: e.target.value })}
            data-testid="cp-input-nova"
          />
          <input
            type="password" required className={inputCls} placeholder="Confirmar nova senha"
            value={form.confirmar} onChange={(e) => setForm({ ...form, confirmar: e.target.value })}
            data-testid="cp-input-confirmar"
          />
          <button
            type="submit" disabled={saving} data-testid="cp-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-emerald-950 font-bold px-6 py-4 rounded-full transition-colors"
          >
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <KeyRound className="w-4 h-4" />}
            Salvar nova senha
          </button>
        </form>
      </div>

      {role === "admin" && <UsersSection onUnauthorized={onUnauthorized} />}

      {role === "admin" && (
      <form onSubmit={saveSim} className="glass rounded-3xl p-8 space-y-4" data-testid="sim-config-form">
        <p className="text-sm text-slate-400 flex items-center gap-2">
          <Calculator className="w-4 h-4 text-emerald-400" />
          Simulador de financiamento do site.
        </p>
        <div>
          <label className="text-xs text-slate-400 block mb-1.5">Taxa de juros (% ao mês)</label>
          <input
            type="number" step="0.01" min="0.01" max="10" required className={inputCls} placeholder="Ex: 1,49"
            value={sim.taxa_am} onChange={(e) => setSim({ ...sim, taxa_am: e.target.value })}
            data-testid="sim-config-taxa"
          />
        </div>
        <div>
          <label className="text-xs text-slate-400 block mb-1.5">Taxa de contrato — TAC (R$)</label>
          <input
            type="number" step="10" min="0" required className={inputCls} placeholder="Ex: 1200"
            value={sim.tac} onChange={(e) => setSim({ ...sim, tac: e.target.value })}
            data-testid="sim-config-tac"
          />
          <p className="text-[11px] text-slate-500 mt-1.5 px-1">TAC e IOF são financiados junto ao contrato e já aparecem no detalhamento da simulação.</p>
        </div>
        <button
          type="submit" disabled={savingSim} data-testid="sim-config-save"
          className="w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-emerald-950 font-bold px-6 py-4 rounded-full transition-colors"
        >
          {savingSim ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-4 h-4" />}
          Salvar simulador
        </button>
      </form>
      )}

      {role === "admin" && (
        <div className="glass rounded-3xl p-8 space-y-4" data-testid="ml-integration-section">
          <p className="text-sm text-slate-400 flex items-center gap-2">
            <Store className="w-4 h-4 text-emerald-400" />
            Integração Mercado Livre — publique veículos do Estoque direto no marketplace.
          </p>
          {mlStatus === null ? (
            <div className="h-12 rounded-xl bg-[#131620] animate-pulse" />
          ) : mlStatus.connected ? (            <div className="space-y-3">
              <p className="text-sm text-emerald-400 flex items-center gap-2" data-testid="ml-status-connected">
                <span className="w-2 h-2 rounded-full bg-emerald-400" />
                Conta conectada (usuário ML {mlStatus.ml_user_id})
              </p>
              <button
                onClick={disconnectMl} disabled={mlBusy} data-testid="ml-disconnect-button"
                className="w-full inline-flex items-center justify-center gap-2 bg-red-500/10 hover:bg-red-500/20 disabled:opacity-60 text-red-400 font-semibold px-6 py-3 rounded-full transition-colors"
              >
                {mlBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlink className="w-4 h-4" />}
                Desconectar conta
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-slate-400 flex items-center gap-2" data-testid="ml-status-disconnected">
                <span className="w-2 h-2 rounded-full bg-slate-500" />
                Nenhuma conta conectada.
              </p>
              <button
                onClick={connectMl} disabled={mlBusy} data-testid="ml-connect-button"
                className="w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-emerald-950 font-bold px-6 py-3 rounded-full transition-colors"
              >
                {mlBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                Conectar Mercado Livre
              </button>
            </div>
          )}
          <form onSubmit={saveLoja} className="space-y-3 pt-3 border-t border-slate-800/60" data-testid="loja-form">
            <p className="text-xs text-slate-500">Endereço da loja — o Mercado Livre exige cidade/UF nos anúncios de veículos.</p>
            <div className="grid grid-cols-6 gap-3">
              <input className={`${inputCls} col-span-3`} required placeholder="Cidade*" value={loja.cidade} onChange={(e) => setLoja({ ...loja, cidade: e.target.value })} data-testid="loja-input-cidade" />
              <input className={`${inputCls} col-span-1`} required maxLength={2} placeholder="UF*" value={loja.uf} onChange={(e) => setLoja({ ...loja, uf: e.target.value.toUpperCase() })} data-testid="loja-input-uf" />
              <input className={`${inputCls} col-span-2`} placeholder="CEP" value={loja.cep} onChange={(e) => setLoja({ ...loja, cep: e.target.value })} data-testid="loja-input-cep" />
            </div>
            <button
              type="submit" disabled={savingLoja} data-testid="loja-save-button"
              className="inline-flex items-center gap-2 glass hover:border-emerald-500/40 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors"
            >
              {savingLoja ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 text-emerald-400" />}
              Salvar endereço
            </button>
          </form>
        </div>
      )}
    </div>
  );
};
